import React, { useRef, useState, useEffect, useCallback } from 'react';
import { formatDate } from '../../utils/dateUtils';
import { Button } from '../ui';
import { useServices } from '../../contexts/ServiceContext';
import { useProfessionals } from '../../contexts/ProfessionalContext';
import { useAppointmentValues, useSafeAreaInsets, useModalControls, useAppointmentModal } from '../../hooks';
import { getProfessionalName } from '../../utils/entityUtils';
import AdminMobileAppointmentForm from './MobileAppointmentForm';
import AdminBookingForm from './booking/AdminBookingForm';

/**
 * Modal responsivo para exibição de detalhes do agendamento
 * Versão mobile: tela cheia com scroll interno
 * Versão desktop: modal centralizado
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.appointment - Dados do agendamento
 * @param {boolean} props.isOpen - Estado de abertura do modal
 * @param {Function} props.onClose - Função para fechar o modal
 * @param {Function} props.onReschedule - Função para remarcar o agendamento
 * @param {Function} props.onCancel - Função para cancelar o agendamento
 * @param {Function} props.onEditServices - Função para editar serviços
 * @param {Function} props.onMarkAsPaid - Função para marcar como pago
 * @returns {JSX.Element} - Componente React
 */
const AppointmentDetailModal = ({
  appointment,
  isOpen,
  onClose,
  onReschedule,
  onCancel,
  onEditServices,
  onMarkAsPaid
}) => {
  // Detectar se é mobile
  const isMobile = useRef(
    (window.innerWidth < 768) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
  
  // Hooks para obtenção de dados
  const { services } = useServices();
  const { professionals } = useProfessionals();
  const { formatAppointmentValue } = useAppointmentValues();
  const safeAreaInsets = useSafeAreaInsets();
  
  // Hook para controles do modal
  const {
    modalRef,
    contentRef,
    modalRenderIdRef,
    handleClose: handleModalClose
  } = useModalControls({ 
    isOpen, 
    isMobile: isMobile.current 
  });
  
  // Hook para lógica de negócio do modal
  const {
    appointmentData,
    selectedServices,
    isSubmitting,
    errorMessage,
    activeScreen,
    showProfessionalConfirmation,
    showProfessionalSelection,
    showPaymentTypeSelection,
    selectedProfessionalId,
    customPrice,
    setActiveScreen,
    setSelectedProfessionalId,
    setShowProfessionalConfirmation,
    setShowProfessionalSelection,
    setShowPaymentTypeSelection,
    setCustomPrice,
    formattedTime,
    formatPhoneNumber,
    isPaid,
    isPaidInAdvance,
    servicesList,
    professionalName,
    handleClose,
    handleCancelAction,
    handleConfirmCancel,
    handleMarkAsPaid,
    handleServicePerformed,
    handlePrePayment,
    handleConfirmServiceCompletion,
    confirmOriginalProfessional,
    showDifferentProfessionalSelection,
    confirmDifferentProfessional,
    handleSaveReschedule,
    handleSaveServices
  } = useAppointmentModal({
    appointment,
    isOpen,
    onReschedule,
    onCancel,
    onEditServices,
    onMarkAsPaid,
    onClose,
    services,
    professionals
  });
  
  // Detectar iOS para ajustes específicos
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  // Definir opções de preço
  const [priceOption, setPriceOption] = useState('default');
  const originalPrice = appointment?.valorTotal || 0;

  // Resetar opções quando o modal é fechado
  useEffect(() => {
    if (!showProfessionalSelection) {
      setPriceOption('default');
      setCustomPrice(null);
    }
  }, [showProfessionalSelection, setCustomPrice]);
  
  // Estado para controlar a exibição do formulário de reagendamento/edição
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingFormMode, setBookingFormMode] = useState('new');
  
  // Manipulador de reagendamento simplificado
  const handleRescheduleAction = useCallback(() => {
    console.log('[AppointmentDetailModal] handleRescheduleAction - appointmentData:', appointmentData);
    console.log('[AppointmentDetailModal] handleRescheduleAction - appointment original:', appointment);
    
    // Verificar o tipo de dados dos serviços
    if (appointment.servicos) {
      console.log('[AppointmentDetailModal] handleRescheduleAction - tipo de servicos:', 
        typeof appointment.servicos, 
        Array.isArray(appointment.servicos),
        appointment.servicos
      );
    }
    
    setBookingFormMode('reschedule');
    setShowBookingForm(true);
  }, [appointmentData, appointment]);
  
  // Manipulador de edição de serviços simplificado
  const handleEditServicesAction = useCallback(() => {
    setBookingFormMode('edit-services');
    setShowBookingForm(true);
  }, []);
  
  // Manipulador para salvar as alterações do reagendamento
  const handleSaveBooking = useCallback((formData) => {
    if (bookingFormMode === 'reschedule') {
      console.log('[AppointmentDetailModal] handleSaveBooking - formData recebido:', formData);
      handleSaveReschedule(formData);
    } else if (bookingFormMode === 'edit-services') {
      console.log('[AppointmentDetailModal] handleSaveBooking - formData para edição:', formData);
      handleSaveServices(formData);
    }
    
    setShowBookingForm(false);
  }, [bookingFormMode, handleSaveReschedule, handleSaveServices]);
  
  if (!isOpen || !appointment) {
    // Não renderizar nada quando o modal não estiver aberto ou sem dados
    return null;
  }
  
  // Formatar valor do agendamento
  const formattedValue = formatAppointmentValue(appointment);
  
  // Renderiza o modal de confirmação de profissional
  const renderProfessionalConfirmation = () => {
    if (!showProfessionalConfirmation) return null;
    
    const isCompletingAdvancedPayment = isPaidInAdvance && !appointment.profissionalConfirmadoId;
    
    return (
      <div className="fixed inset-0 z-[999999] overflow-y-auto flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 transform transition-all">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {isCompletingAdvancedPayment ? 'Confirmar Realização do Serviço' : 'Confirmar Profissional'}
            </h3>
          </div>
          
          <div className="px-6 py-4">
            <p className="text-gray-700 mb-4">
              {isCompletingAdvancedPayment 
                ? `O serviço para ${appointment.cliente} foi realizado pelo profissional ${professionalName} conforme agendado?`
                : `O serviço foi realizado pelo profissional ${professionalName} conforme agendado?`
              }
            </p>
            
            <div className="flex flex-col gap-3 mt-6">
              <Button
                onClick={confirmOriginalProfessional}
                disabled={isSubmitting}
                className="w-full"
              >
                {isCompletingAdvancedPayment ? 'Sim, confirmar realização' : 'Sim, confirmar pagamento'}
              </Button>
              
              <Button
                onClick={showDifferentProfessionalSelection}
                variant="outline"
                disabled={isSubmitting}
                className="w-full"
              >
                Não, outro profissional realizou o serviço
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Renderiza o modal de seleção de profissional
  const renderProfessionalSelection = () => {
    if (!showProfessionalSelection) return null;
    
    const isCompletingAdvancedPayment = isPaidInAdvance && !appointment.profissionalConfirmadoId;
    
    return (
      <div className="fixed inset-0 z-[999999] overflow-y-auto flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 transform transition-all">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {isCompletingAdvancedPayment ? 'Registrar Realização do Serviço' : 'Selecionar Profissional'}
            </h3>
          </div>
          
          <div className="px-6 py-4">
            <p className="text-gray-700 mb-4">
              Selecione o profissional que realizou o serviço:
            </p>
            
            <div className="space-y-3 mt-4 mb-6">
              {professionals?.filter(p => {
                // Verificar se o appointment tem profissional antes de comparar
                if (!appointment?.profissional) return true;
                
                const profissionalId = typeof appointment.profissional === 'object' 
                  ? appointment.profissional?.id 
                  : appointment.profissional;
                  
                return p.id !== profissionalId;
              })
                .map(professional => (
                  <div 
                    key={professional.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      selectedProfessionalId === professional.id 
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    onClick={() => setSelectedProfessionalId(professional.id)}
                  >
                    <div className="flex items-center">
                      <div className="mr-3">
                        <span className={`inline-block w-5 h-5 rounded-full border ${
                          selectedProfessionalId === professional.id
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}>
                          {selectedProfessionalId === professional.id && (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{professional.nome}</h4>
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>

            {selectedProfessionalId && (
              <div className="mt-6 border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Preço cobrado ao cliente:</h4>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="price-default"
                      name="price-option"
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      checked={priceOption === 'default'}
                      onChange={() => {
                        setPriceOption('default');
                        setCustomPrice(null);
                      }}
                    />
                    <label htmlFor="price-default" className="ml-2 block text-sm text-gray-900">
                      Preço padrão: R$ {originalPrice.toFixed(2).replace('.', ',')}
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="price-custom"
                      name="price-option"
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      checked={priceOption === 'custom'}
                      onChange={() => {
                        setPriceOption('custom');
                        setCustomPrice(originalPrice); // Iniciar com valor original
                      }}
                    />
                    <label htmlFor="price-custom" className="ml-2 block text-sm text-gray-900">
                      Preço personalizado
                    </label>
                  </div>
                </div>

                {priceOption === 'custom' && (
                  <div className="mt-3">
                    <div className="flex rounded-md shadow-sm">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                        R$
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={customPrice || ''}
                        onChange={(e) => setCustomPrice(parseFloat(e.target.value) || 0)}
                        className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-between mt-6">
              <Button 
                onClick={() => {
                  setShowProfessionalSelection(false);
                  setShowProfessionalConfirmation(true);
                  setSelectedProfessionalId(null);
                }} 
                variant="outline"
              >
                Voltar
              </Button>
              
              <Button
                onClick={confirmDifferentProfessional}
                disabled={!selectedProfessionalId || isSubmitting}
              >
                {isSubmitting 
                  ? 'Processando...' 
                  : (isCompletingAdvancedPayment ? 'Confirmar Realização' : 'Confirmar')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Renderiza o modal de seleção do tipo de pagamento
  const renderPaymentTypeSelection = () => {
    if (!showPaymentTypeSelection) return null;
    
    return (
      <div className="fixed inset-0 z-[999999] overflow-y-auto flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 transform transition-all">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Tipo de Pagamento</h3>
          </div>
          
          <div className="px-6 py-4">
            <p className="text-gray-700 mb-4">
              O serviço já foi realizado para o cliente <strong>{appointment.cliente}</strong>?
            </p>
            
            <div className="flex flex-col gap-3 mt-6">
              <Button
                onClick={handleServicePerformed}
                disabled={isSubmitting}
                className="w-full"
              >
                Sim, o serviço já foi realizado
              </Button>
              
              <Button
                onClick={handlePrePayment}
                variant="outline"
                disabled={isSubmitting}
                className="w-full"
              >
                Não, cliente está pagando antecipadamente
              </Button>
              
              <Button 
                onClick={() => setShowPaymentTypeSelection(false)} 
                variant="subtle"
                className="mt-2"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Quando mostrar diálogos de confirmação, não renderizar o conteúdo principal
  if (showProfessionalConfirmation || showProfessionalSelection || showPaymentTypeSelection) {
    // Apenas mostrar o backdrop, os diálogos serão renderizados separadamente
    return (
      <>
        <div className="fixed inset-0 z-[99999] bg-black bg-opacity-50"></div>
        
        {/* Renderizar os modais */}
        {renderProfessionalConfirmation()}
        {renderProfessionalSelection()}
        {renderPaymentTypeSelection()}
      </>
    );
  }

  // Renderizar o formulário de agendamento quando ativo
  if (showBookingForm) {
    // Adicionar o ID do agendamento atual para excluir dos conflitos
    const enhancedInitialData = {
      ...appointmentData,
      // Garantir que o ID do agendamento seja passado
      id: appointment.id,
      // Adicionar essas flags importantes para remarcação
      excludeAppointmentId: appointment.id, 
      allowConflicts: true,         // Permitir que o admin force remarcações
      skipAvailabilityCheck: false  // Manter verificação, mas excluindo o próprio agendamento
    };

    // Log para verificar como os serviços estão sendo passados
    console.log('[AppointmentDetailModal] Serviços do appointment:', appointment.servicos);
    console.log('[AppointmentDetailModal] Tipo dos serviços:', typeof appointment.servicos);
    console.log('[AppointmentDetailModal] enhancedInitialData antes da correção:', enhancedInitialData);

    // Garantir que serviços seja sempre um array
    if (enhancedInitialData.servicos && !Array.isArray(enhancedInitialData.servicos)) {
      // Se for um objeto único, converter para array
      if (typeof enhancedInitialData.servicos === 'object' && enhancedInitialData.servicos !== null) {
        enhancedInitialData.servicos = [enhancedInitialData.servicos];
      } 
      // Se for uma string ou número, converter para array com esse valor
      else if (enhancedInitialData.servicos) {
        enhancedInitialData.servicos = [enhancedInitialData.servicos];
      } 
      // Se não estiver definido ou for nulo, criar array vazio
      else {
        enhancedInitialData.servicos = [];
      }
    }
    
    // Verificar se ainda existe servicos e é um valor único não-array depois da primeira tentativa (caso específico)
    if (typeof enhancedInitialData.servicos === 'number' || 
        typeof enhancedInitialData.servicos === 'string') {
      console.log('[AppointmentDetailModal] Corrigindo servicos que ainda é um valor único:', enhancedInitialData.servicos);
      enhancedInitialData.servicos = [enhancedInitialData.servicos];
    }
    
    // Verificar se não existe o campo servicos, mas existe servicosIds
    if ((!enhancedInitialData.servicos || enhancedInitialData.servicos.length === 0) && 
        enhancedInitialData.servicosIds) {
      console.log('[AppointmentDetailModal] Usando servicosIds como fallback:', enhancedInitialData.servicosIds);
      enhancedInitialData.servicos = Array.isArray(enhancedInitialData.servicosIds) 
        ? enhancedInitialData.servicosIds
        : [enhancedInitialData.servicosIds];
    }
    
    // Verificar se o appointment tem o campo selectedServices e usar como fallback
    if ((!enhancedInitialData.servicos || enhancedInitialData.servicos.length === 0) && 
        appointment.selectedServices) {
      console.log('[AppointmentDetailModal] Usando selectedServices como fallback:', appointment.selectedServices);
      enhancedInitialData.servicos = Array.isArray(appointment.selectedServices) 
        ? appointment.selectedServices
        : [appointment.selectedServices];
    }

    console.log('[AppointmentDetailModal] enhancedInitialData após correção:', enhancedInitialData);
    
    // Criar um novo objeto para evitar problemas de referência
    const cleanedData = { ...enhancedInitialData };
    
    // Log detalhado de chaves existentes para diagnóstico
    console.log('[AppointmentDetailModal] Chaves disponíveis em cleanedData:', Object.keys(cleanedData));
    
    // Verificar se temos servicosDetalhes (comum em alguns formatos de dados)
    if (cleanedData.servicosDetalhes && Array.isArray(cleanedData.servicosDetalhes)) {
      console.log('[AppointmentDetailModal] Usando servicosDetalhes:', cleanedData.servicosDetalhes);
      
      // Extrair IDs dos servicosDetalhes
      const servicosIds = cleanedData.servicosDetalhes.map(servico => 
        typeof servico === 'object' && servico !== null ? servico.id : servico
      );
      
      console.log('[AppointmentDetailModal] IDs extraídos de servicosDetalhes:', servicosIds);
      
      // Garantir que o campo servicos exista e contenha os IDs corretos
      cleanedData.servicos = servicosIds;
    }
    // Tratamento para o campo serviços
    else if (cleanedData.servicos) {
      // Extrair o ID do serviço se for um objeto
      if (Array.isArray(cleanedData.servicos) && cleanedData.servicos.length > 0) {
        // Verificar se são objetos com ID ou strings/valores diretos
        const servicosIds = cleanedData.servicos.map(servico => 
          typeof servico === 'object' && servico !== null ? servico.id : servico
        );
        
        console.log('[AppointmentDetailModal] IDs de serviços extraídos de servicos:', servicosIds);
        
        // Substituir o array de objetos por um array de IDs
        cleanedData.servicos = servicosIds;
      }
    }
    
    // IMPORTANTE: Garantir que servicos seja um array e não um número único
    if (cleanedData.servicos && !Array.isArray(cleanedData.servicos)) {
      console.log('[AppointmentDetailModal] Convertendo servicos não-array para array:', cleanedData.servicos);
      cleanedData.servicos = [cleanedData.servicos];
    }
    
    // Verificação final para garantir que temos um array de serviços
    if (!cleanedData.servicos || !Array.isArray(cleanedData.servicos)) {
      console.log('[AppointmentDetailModal] Criando array de serviços padrão');
      
      // Tentar obter um ID a partir de qualquer fonte disponível
      let servicoId = null;
      
      if (cleanedData.servicosDetalhes && Array.isArray(cleanedData.servicosDetalhes) && cleanedData.servicosDetalhes.length > 0) {
        servicoId = (typeof cleanedData.servicosDetalhes[0] === 'object') 
          ? cleanedData.servicosDetalhes[0].id 
          : cleanedData.servicosDetalhes[0];
      }
      
      cleanedData.servicos = servicoId ? [servicoId] : [];
    }
    
    console.log('[AppointmentDetailModal] cleanedData final para AdminBookingForm:', cleanedData);
    console.log('[AppointmentDetailModal] servicos no cleanedData final:', cleanedData.servicos);
    
    // Obter o salonId do appointment, dos serviços ou dos profissionais
    const getEffectiveSalonId = () => {
      // 1. Tentar obter do agendamento
      if (appointment && appointment.salonId) {
        console.log(`[AppointmentDetailModal] Usando salonId do agendamento: ${appointment.salonId}`);
        return appointment.salonId;
      }
      
      // 2. Tentar obter dos serviços
      if (services && services.length > 0 && services[0].salonId) {
        console.log(`[AppointmentDetailModal] Usando salonId do primeiro serviço: ${services[0].salonId}`);
        return services[0].salonId;
      }
      
      // 3. Tentar obter dos profissionais
      if (professionals && professionals.length > 0 && professionals[0].salonId) {
        console.log(`[AppointmentDetailModal] Usando salonId do primeiro profissional: ${professionals[0].salonId}`);
        return professionals[0].salonId;
      }
      
      // 4. Tentar obter da URL
      const urlMatches = [
        window.location.pathname.match(/\/admin\/([^\/]+)/),
        window.location.pathname.match(/\/salon\/([^\/]+)/)
      ];
      
      for (const match of urlMatches) {
        if (match && match[1]) {
          console.log(`[AppointmentDetailModal] Usando salonId da URL: ${match[1]}`);
          return match[1];
        }
      }
      
      console.warn('[AppointmentDetailModal] Não foi possível determinar salonId!');
      return null;
    };
    
    const effectiveSalonId = getEffectiveSalonId();
    console.log(`[AppointmentDetailModal] salonId efetivo para remarcação: ${effectiveSalonId}`);
    
    return (
      <AdminBookingForm
        isOpen={true}
        onClose={() => {
          // Esconder o formulário de reagendamento
          setShowBookingForm(false);
          // Resetar o modo
          setBookingFormMode('new');
          // Voltar para a tela de detalhes
          setActiveScreen('details');
        }}
        onSubmit={handleSaveBooking}
        initialData={cleanedData}
        mode={bookingFormMode}
        services={services}
        professionals={professionals}
        salonId={effectiveSalonId}
      />
    );
  }

  // Renderiza o conteúdo apropriado para a tela ativa
  const renderActiveScreen = () => {
    switch (activeScreen) {
      case 'confirmCancel':
        return (
          <>
            <div className="flex-shrink-0 sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center z-10">
              <h3 className="text-lg font-semibold text-gray-800">Cancelar Agendamento</h3>
              <button 
                onClick={handleClose} 
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div ref={contentRef} className="flex-grow overflow-y-auto p-6" data-content-scrollable="true">
              {errorMessage && (
                <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md">
                  {errorMessage}
                </div>
              )}
              
              <div className="text-center p-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-500 mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                
                <h3 className="text-xl font-medium text-gray-900 mb-2">Confirmar Cancelamento</h3>
                <p className="text-gray-600 mb-6">
                  Tem certeza que deseja cancelar o agendamento de <strong>{appointment.cliente}</strong> em <strong>{formatDate(appointment.data)} às {formattedTime}</strong>?
                </p>
                
                <div className="flex justify-center space-x-4">
                  <Button
                    onClick={() => setActiveScreen('details')}
                    variant="subtle"
                    size="sm"
                    className="py-2"
                  >
                    Voltar
                  </Button>
                  
                  <Button
                    onClick={handleConfirmCancel}
                    variant="subtle"
                    size="sm"
                    disabled={isSubmitting}
                    className="py-2"
                  >
                    {isSubmitting ? 'Cancelando...' : 'Confirmar Cancelamento'}
                  </Button>
                </div>
              </div>
            </div>
          </>
        );
        
      default: // details
        return (
          <>
            <div className="flex-shrink-0 sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center z-10">
              <h3 className="text-lg font-semibold text-gray-800">Detalhes do Agendamento</h3>
              <button 
                onClick={handleClose} 
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div ref={contentRef} className="flex-grow overflow-y-auto p-6" data-content-scrollable="true">
              {/* Informações do cliente */}
              <div className="mb-5">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h4 className="text-xl font-bold text-gray-800">{appointment.cliente}</h4>
                </div>
                
                <div className="flex flex-wrap gap-2 mt-2">
                  {appointment.cancelado ? (
                    <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                      <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-red-500"></span>
                      Cancelado
                    </div>
                  ) : appointment.profissionalConfirmadoId ? (
                    <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                      <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-blue-500"></span>
                      Serviço realizado
                    </div>
                  ) : (
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      appointment.confirmado 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                    }`}>
                      <span className={`w-1.5 h-1.5 mr-1.5 rounded-full ${
                        appointment.confirmado ? 'bg-green-500' : 'bg-yellow-500'
                      }`}></span>
                      {appointment.confirmado ? 'Cliente confirmado' : 'Cliente não confirmado'}
                    </div>
                  )}
                  
                  <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    isPaid 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : 'bg-gray-100 text-gray-800 border border-gray-200'
                  }`}>
                    <span className={`w-1.5 h-1.5 mr-1.5 rounded-full ${
                      isPaid ? 'bg-green-500' : 'bg-gray-500'
                    }`}></span>
                    {isPaid 
                      ? (appointment.pagamentoAntecipado ? 'Pago antecipado' : 'Pago') 
                      : 'Pagamento Pendente'}
                  </div>
                </div>
              </div>
              
              {/* Grid de informações - em desktop mostra 2 colunas, em mobile 1 coluna */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Coluna 1 */}
                <div className="space-y-4">
                  <div>
                    <h5 className="text-sm font-medium text-gray-500">Data e Hora</h5>
                    <p className="text-base text-gray-800">{formatDate(appointment.data)} às {formattedTime}</p>
                  </div>
                  
                  <div>
                    <h5 className="text-sm font-medium text-gray-500">Serviços</h5>
                    <p className="text-base text-gray-800">{servicesList}</p>
                  </div>
                  
                  <div>
                    <h5 className="text-sm font-medium text-gray-500">Profissional</h5>
                    {appointment.profissionalConfirmadoId ? (
                      <>
                        <p className="text-base font-bold text-gray-800">
                          {getProfessionalName(appointment.profissionalConfirmadoId, professionals)}
                        </p>
                        <p className="text-sm text-gray-500">
                          (profissional que realizou o serviço)
                        </p>
                        <p className="text-sm text-gray-500">
                          {appointment.profissional 
                            ? `Agendado originalmente com: ${professionalName}` 
                            : "Agendamento original: Qualquer profissional"}
                        </p>
                      </>
                    ) : (
                      <p className="text-base text-gray-800">
                        {appointment.profissional ? professionalName : "Qualquer profissional"}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Coluna 2 */}
                <div className="space-y-4">
                  <div>
                    <h5 className="text-sm font-medium text-gray-500">Valor</h5>
                    {appointment.valorCustomizado !== undefined && appointment.valorCustomizado !== null ? (
                      <>
                        <p className="text-xl font-bold text-gray-800">
                          {new Intl.NumberFormat('pt-BR', { 
                            style: 'currency', 
                            currency: 'BRL' 
                          }).format(appointment.valorCustomizado)}
                        </p>
                        <p className="text-sm text-gray-500">
                          (valor cobrado personalizado)
                        </p>
                        <p className="text-sm text-gray-500">
                          Valor original: {formattedValue}
                        </p>
                      </>
                    ) : (
                    <p className="text-xl font-bold text-gray-800">{formattedValue}</p>
                    )}
                  </div>
                  
                  <div>
                    <h5 className="text-sm font-medium text-gray-500">Contato</h5>
                    <p className="text-base text-gray-800">
                      <a href={`tel:${appointment.whatsapp}`} className="text-blue-600 hover:underline">
                        {formatPhoneNumber(appointment.whatsapp)}
                      </a>
                    </p>
                    {appointment.email && (
                      <p className="text-base text-gray-800">
                        <a href={`mailto:${appointment.email}`} className="text-blue-600 hover:underline">
                          {appointment.email}
                        </a>
                      </p>
                    )}
                  </div>
                  
                  {appointment.observacoes && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-500">Observações</h5>
                      <p className="text-sm text-gray-700">{appointment.observacoes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Botões de ação - Layout responsivo */}
            <div className="flex-shrink-0 pt-4 px-6 pb-6 border-t border-gray-200">
              <h5 className="text-sm font-medium text-gray-500 mb-3">Ações</h5>
              
              {/* Versão mobile: grade responsiva de 2 colunas com botões maiores */}
              <div className="grid grid-cols-2 gap-3 md:hidden">
                <Button
                  onClick={isPaid 
                    ? (isPaidInAdvance && !appointment.profissionalConfirmadoId ? handleConfirmServiceCompletion : null) 
                    : handleMarkAsPaid}
                  disabled={(isPaid && (!isPaidInAdvance || appointment.profissionalConfirmadoId)) || isSubmitting || appointment.cancelado}
                  variant="subtle"
                  size="sm"
                  className={`${(isPaid && (!isPaidInAdvance || appointment.profissionalConfirmadoId)) || appointment.cancelado ? "opacity-50" : ""} py-2`}
                >
                  {isPaid 
                    ? (isPaidInAdvance 
                        ? (appointment.profissionalConfirmadoId ? 'Pago antecipado' : 'Confirmar realização') 
                        : 'Já Pago') 
                    : 'Marcar Pago'}
                </Button>
                
                <Button
                  onClick={handleRescheduleAction}
                  variant="subtle"
                  size="sm"
                  disabled={isSubmitting}
                  className="py-2"
                >
                  Remarcar
                </Button>
                
                <Button
                  onClick={handleEditServicesAction}
                  variant="subtle"
                  size="sm"
                  disabled={isSubmitting || appointment.cancelado}
                  className={`${appointment.cancelado ? "opacity-50" : ""} py-2`}
                >
                  Editar Serviços
                </Button>
                
                <Button
                  onClick={handleCancelAction}
                  variant="subtle"
                  size="sm"
                  disabled={isSubmitting || appointment.cancelado}
                  className={`${appointment.cancelado ? "opacity-50" : ""} py-2`}
                >
                  Cancelar
                </Button>
              </div>
              
              {/* Desktop: botões de texto estilo link em linha */}
              <div className="hidden md:flex md:flex-wrap md:items-center md:gap-2">
                {appointment.cancelado ? (
                  <>
                    <span className="text-red-600 text-sm font-medium mr-2">Agendamento cancelado</span>
                    <span className="text-gray-300">|</span>
                    <Button
                      onClick={handleRescheduleAction}
                      variant="link"
                      size="sm"
                      disabled={isSubmitting}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Remarcar
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={isPaid 
                        ? (isPaidInAdvance && !appointment.profissionalConfirmadoId ? handleConfirmServiceCompletion : null) 
                        : handleMarkAsPaid}
                      disabled={(isPaid && (!isPaidInAdvance || appointment.profissionalConfirmadoId)) || isSubmitting}
                      variant="link"
                      size="sm"
                      className={(isPaid && (!isPaidInAdvance || appointment.profissionalConfirmadoId)) ? "opacity-50" : "text-green-600 hover:text-green-800"}
                    >
                      {isPaid 
                        ? (isPaidInAdvance 
                            ? (appointment.profissionalConfirmadoId ? 'Pago antecipado' : 'Confirmar realização') 
                            : 'Já Pago') 
                        : 'Marcar como Pago'}
                    </Button>
                    
                    <span className="text-gray-300">|</span>
                    
                    <Button
                      onClick={handleRescheduleAction}
                      variant="link"
                      size="sm"
                      disabled={isSubmitting}
                    >
                      Remarcar
                    </Button>
                    
                    <span className="text-gray-300">|</span>
                    
                    <Button
                      onClick={handleEditServicesAction}
                      variant="link"
                      size="sm"
                      disabled={isSubmitting}
                      className="text-purple-600 hover:text-purple-800"
                    >
                      Alterar Serviços
                    </Button>
                    
                    <span className="text-gray-300">|</span>
                    
                    <Button
                      onClick={handleCancelAction}
                      variant="link"
                      size="sm"
                      disabled={isSubmitting}
                      className="text-red-600 hover:text-red-800"
                    >
                      Cancelar Agendamento
                    </Button>
                  </>
                )}
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <>
      {/* Modal principal */}
      <div 
        className="fixed inset-0 z-[99999] overflow-hidden md:bg-black md:bg-opacity-50 bg-transparent flex items-end md:items-center justify-center"
        style={{
          WebkitTransform: 'translateZ(0)',
          transform: 'translateZ(0)',
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            // Em telas desktop, fechar ao clicar no backdrop
            if (!isMobile.current) {
              handleClose();
            }
          }
        }}
      >
        {/* Container do modal - tela cheia em mobile, e centralizado em desktop */}
        <div 
          ref={modalRef}
          className={`
            bg-white md:rounded-xl shadow-xl 
            transform transition-all duration-300 ease-out 
            w-full h-full md:h-auto md:w-auto md:max-w-2xl
            flex flex-col modal-scroll-container
            ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full md:translate-y-10 opacity-0'}
          `}
          style={{
            WebkitTransform: isOpen ? 'translateY(0)' : 'translateY(100%)',
            transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
            paddingTop: (isMobile.current && activeScreen !== 'reschedule' && activeScreen !== 'editServices') ? (isIOS ? `${safeAreaInsets.top}px` : 'env(safe-area-inset-top)') : '0',
            paddingBottom: isMobile.current ? '0' : '0',
            overscrollBehavior: 'contain',
          }}
        >
          <div ref={contentRef} id="modal-content" className="flex flex-col h-full overflow-auto" data-content-scrollable="true">
            {renderActiveScreen()}
          </div>
        </div>
      </div>
      
      {/* Overlay branco para mobile - agora separado e simplificado */}
      {isMobile.current && (
        <div 
          className="fixed inset-0 z-[99990] bg-white"
          style={{
            width: '100vw', 
            height: '100vh',
            margin: 0,
            padding: 0,
            pointerEvents: 'none',
          }}
        />
      )}
    </>
  );
};

export default AppointmentDetailModal; 